import { forwardRef, useContext, useMemo } from 'react'
import { ChooseTreatmentContext, LabelData, Product } from '../FreeDosageLayoutV2';

import { formatCannabisDisplayData, calculatePricePerGram } from '../../helpersV2';
import { clientConfig } from '../../../../config';
import Disclaimer from '../../components/Disclaimers/Disclaimer';
import LabelCatalogTag from './LabelCatalogTag';
import { getPrice } from '../../../../utils/translator';
import * as constants from "../constants";
import * as translator from '../../../../utils/translator';
import { MainTracker } from '../../../../PixelTrackers/MainTracker';
import { useCustomer } from '../../../../customHooks/useCustomer';


interface Props {
    id: string;
    product: Product;
    price: string;
    flow?: string;
    containerClassName?: string;
    labelData:LabelData | null;
}

const ProductCard = forwardRef<HTMLDivElement, Props>(({ id, product, flow, containerClassName, labelData }, ref) => {

    const chooseTreatmentContext = useContext(ChooseTreatmentContext);
    const { customerState } = useCustomer();
    const disableUpQuantity = chooseTreatmentContext?.disableUpQuantity;
    const isMaxNumOfProductSelected = chooseTreatmentContext?.selectedProducts.length == chooseTreatmentContext?.maxCartProducts;
    const selectedProductItem = chooseTreatmentContext?.selectedProductItems.find((selectedItem) => selectedItem.catalog_id === product.catalog_id);
    const selectedProductForDetails = chooseTreatmentContext?.selectedProductForDetails;
    const allPharmaciesLocations = chooseTreatmentContext?.allPharmaciesLocations;
    const desiredProductItem = selectedProductItem;
    const isOpen = selectedProductForDetails?.product_id === product.product_id;
    const additionalDataObj = typeof product.additional_data === 'string' ? JSON.parse(product.additional_data) : product.additional_data || {};
    const shippingMethod = chooseTreatmentContext?.shippingMethod;
    const selectedService = chooseTreatmentContext?.selectedService;
    const filteredPharmaciesForPickup = chooseTreatmentContext?.filteredPharmaciesForPickup;
    const isProductInStock = product.in_stock || product.express_stock || product.self_pickup_stock;
    const imageUrl = product.image
    ? `${clientConfig.IMAGES_URL}${product.image.base_path}/${encodeURIComponent(product.image.name)}`
    : null;

    // cannabis data display processing
    const { thcDisplay, cbdDisplay, strain } = formatCannabisDisplayData(additionalDataObj);
    const customDosageForCannabis = (additionalDataObj?.thc && additionalDataObj?.cbd) 
      ? `THC: ${thcDisplay} | CBD: ${cbdDisplay}` 
      : '';
    const language = localStorage.getItem("language") || "de";
    const locale = chooseTreatmentContext?.cart?.locale_id || 'de-DE';

    // Use the pre-calculated visible price from the product object
    const pricePerUnit = product.visiblePrice ?? 0;

    // Calculate price per gram
    const getPricePerGram = (): number | null => {
        const currentProductItem = desiredProductItem || product.items[0];
        const currentPricePerUnit = Number(pricePerUnit);
        
        if (isNaN(currentPricePerUnit) || currentPricePerUnit <= 0 || !currentProductItem) {
            return null;
        }
        
        return calculatePricePerGram(currentPricePerUnit, currentProductItem, product.quantity_type);
    };

    const handleOpenProduct = () => {
        // Track product_card_click event
        const userId = customerState?.customerID || null;
        const thcPercent = additionalDataObj?.thc || null;
        const cbdPercent = additionalDataObj?.cbd || null;
        const pricePerGram = getPricePerGram();
        // Extract strain value - formatCannabisDisplayData returns "Sorte: {strain}" or empty string
        const strainValue = additionalDataObj?.strain || additionalDataObj?.genetic || null;
        
        MainTracker.track("custom", "product_card_click", {
            user_id: userId,
            product_id: product.product_id,
            product_name: product.title,
            strain: strainValue,
            thc_percent: thcPercent,
            cbd_percent: cbdPercent,
            manufacturer: product.manufacturer_name || null,
            price_per_gram: pricePerGram
        });

        if (product.isSelected) {
            chooseTreatmentContext?.handleProductDetailsOpen!(product);
        }
        
        else if (!isMaxNumOfProductSelected) {
            
            if (chooseTreatmentContext?.reachedMaxQuota && !isOpen) {
                chooseTreatmentContext?.setShowExceededMaxQuotaModal!(true);
                return;
            }

            chooseTreatmentContext?.handleProductDetailsOpen!(product);
        }
        else if (!product.isSelected) {
            chooseTreatmentContext?.setShowMaxLimitModal!(true);
        }
    }


    const customDosageBlock = useMemo(() => {

        return (
          <div id="product-card-custom-dosage" className={`flex w-full justify-between`}>
            <div className={`flex flex-row justify-between w-full`}>
              <div className="flex flex-col w-full">
                <p
                  id="product-card-custom-dosage-text"
                  style={{ fontFamily: "Roboto, sans-serif" }}
                  className="text-[11px] md:text-[13px] text-[#4D4D4D]"
                >
                  {customDosageForCannabis}
                </p>
                <p
                  id="product-card-custom-dosage-strain"
                  style={{ fontFamily: "Roboto, sans-serif" }}
                  className="text-[11px] md:text-[13px] text-[#4D4D4D]"
                >
                  {strain}
                </p>
              </div>

                {/* Available Pharmacies  */}
                {shippingMethod === constants.PICKUP && (
                <div className="flex flex-col max-w-1/3 justify-end">
                  <p className="text-[11px] text-[#4D4D4D] text-end">
                    {
                      selectedService === constants.SERVICE_PRESCRIPTION_AND_MEDICATION ?
                        translator.translate(language, "choose_treatment", "default/available_at_pharmacy")
                        .replace(":pharmacy_count", product.available_stocks?.filter((stock) => filteredPharmaciesForPickup?.[stock.stockCode])?.length.toString() || "0") 
                      :
                      translator.translate(language, "choose_treatment", "default/available_at_pharmacy")
                        .replace(":pharmacy_count", product.available_external_pharmacies?.filter((stockCode) => filteredPharmaciesForPickup?.[stockCode])?.length.toString() || "0")
                    }
                  </p>
                </div>
                )}
            </div>
            
          </div>
        );
      }, [isOpen, customDosageForCannabis, strain, shippingMethod, selectedService, product.available_stocks, allPharmaciesLocations]);


    return (
            
        <div
            id={id ? id : 'product-card-container'}
            data-product-id={product.product_id}
            onClick={ (e) => {
                    handleOpenProduct();
                    e.currentTarget.style.backgroundColor = product.is_pre_orderable ? '#f7faef' : 'white'
                }
            }
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = isOpen || product.isSelected ? (product.is_pre_orderable ? '#f7faef' : 'white') : (product.is_pre_orderable ? '#eef6e5' : '#FCFCFD')}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = product.is_pre_orderable ? '#f7faef' : 'white'}

            style={{
                borderLeft: selectedProductItem ? "5px solid #269C6E" : product.is_pre_orderable ? "1px solid #269C6E" : "1px solid #FFFFFF",
                ...(product.is_pre_orderable && !selectedProductItem ? { border: "1px solid #269C6E" } : {}),
            }}
            ref={ref}
            className={`flex relative scroll-mt-[100px] ${product.isSelected ? "" : "min-h-[110px]"} ${product.is_pre_orderable ? "bg-[#f7faef]" : "bg-white"} gap-[2px] md:gap-2 p-4 pl-0 md:p-6 rounded-[8px] w-full
                ${isMaxNumOfProductSelected && !product.isSelected ? "cursor-not-allowed" : "cursor-pointer" } ${containerClassName} `}
        >
            {/* Product Image */}
            {imageUrl && (
                <div className="flex justify-center">
                    <img
                    src={imageUrl}
                    alt={product.title}
                    className="w-24 h-24"
                    onError={e => (e.currentTarget.style.display = 'none')}
                    />
                </div>
            )}
            {/* Labels */}
            <div>
            {(
                //hideTags || --removed because of legals
                labelData===null || !(chooseTreatmentContext?.treatAllAsRegular ? false : isProductInStock) )
            ? null 
            : <LabelCatalogTag labelData={labelData} className='absolute -top-2 right-6' />}
            </div>

            {/* Content */}
            <div id='product-card-content' className='flex flex-col w-full justify-between'>

                {/* Header  */}
                <div id='proudct-card-header' className='flex flex-row w-full items-start justify-between gap-1'>

                    <p id="product-card-title" className={`font-semibold leading-[18px] text-[14px] md:text-[16px] text-[#0D2C54] font-roboto ${!chooseTreatmentContext?.treatAllAsRegular && isProductInStock ? 'max-w-[70%]' : 'max-w-[100%]'}`}>{product.title}</p>

                    {product.is_pre_orderable && (
                        <span className="bg-[#61a333] text-white text-[12px] font-normal px-2 py-0.5 rounded-md whitespace-nowrap">
                            {translator.translate(language, 'choose_treatment', 'pre_order_badge')}
                        </span>
                    )}

                    {
                        !chooseTreatmentContext?.treatAllAsRegular ?
                            <div className='flex flex-col'>
                                <p className='text-[#0D2C54] text-lg md:text-xl font-medium flex items-center min-w-fit'>
                                    {getPrice(pricePerUnit, locale)}
                                </p>
                                <p className='w-full text-[10px] md:text-[11px] text-[#616161]'> pro {product.items[0]?.custom_quantity_title?.split(' ')?.[1]} </p>
                            </div>
                            :
                            null
                    }
                </div>

                <div id='product-card-body' className='flex flex-col w-full gap-3'>
                    {/* Custom dosage text  */}
                    {customDosageBlock}
                    
                </div>
                
                {isOpen && disableUpQuantity &&
                    <Disclaimer 
                    text = {(() => {
                      const orderRateViolation = chooseTreatmentContext?.cannabisLimits?.violations.find(v => v.type === 'hourly_order' || v.type === 'daily_order');
                      if (orderRateViolation) {
                        const key = orderRateViolation.type === 'hourly_order' ? 'cannabis_limits/hourly_order_limit' : 'cannabis_limits/daily_order_limit';
                        return translator.translate(language, 'choose_treatment', key).replace('<time>', String(orderRateViolation.retryAfterHours));
                      }
                      return translator.translate(language, 'choose_treatment', 'cannabis_limits/monthly_grams_quota').replace('<maxQuota>', String(chooseTreatmentContext?.cannabisLimits?.monthlyLimit || 200)).replace('<quota>', String(chooseTreatmentContext?.allowedCannabisGramsQuota || 200));
                    })()}
                        containerClassName='mt-3 items-center justify-center'
                        textClassName='font-[400] text-sm items-center'
                        iconClassName='text-[#00B388] -mt-1'
                    />
                }

            </div>
        </div>
    )
});

export default ProductCard